import { PageSize, Provider } from '@/constant';
import { TValueLodging } from '@/pages/workers/lm1-02/lm1-02.config';
import {
	GetProviderInfoListParams,
	GetWorkerLodgingListParams,
	IWorkerLodgingApplicantItem,
	TPayloadUpdateSuccess,
	WorkerLodgingItem,
	WorkerLodgingListItem,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';

import {
	formatWorkerLodgingApplicantList,
	formatWorkerLodgingDetail,
	formatWorkerLodgingList,
	RootState,
} from '..';
import { storage } from '../store.persist';
import {
	IPayloadCreateLodgingApplicantRequest,
	IPayloadCreateLodgingRequest,
	IPayloadDeleteApplicantListByLodgingIdRequest,
	IPayloadDeleteWorkerLodgingByIdRequest,
	IPayloadDeleteWorkerLodgingsRequest,
	IPayloadGetApplicantListByLodgingIdRequest,
	IPayloadGetApplicantListByLodgingIdSuccess,
	IPayloadGetProviderInfoListRequest,
	IPayloadGetProviderInfoListSuccess,
	IPayloadGetWorkerLodgingApplicantListRequest,
	IPayloadGetWorkerLodgingApplicantListSuccess,
	IPayloadGetWorkerLodgingDetailRequest,
	IPayloadGetWorkerLodgingDetailSuccess,
	IPayloadGetWorkerLodgingExcelListRequest,
	IPayloadGetWorkerLodgingListRequest,
	IPayloadGetWorkerLodgingListSuccess,
	IPayloadUpdateLodgingProviderRequest,
	IProviderInfoItemStore,
	IWorkerLodgingApplicantDetailStore,
	IWorkerLodgingDetailStore,
	IWorkerLodgingListItemStore,
	TPayloadUploadExcelLodgingRequest,
	TPayloadUploadExcelLodgingSuccess,
	TUploadExcelLodgingFailedItemStore,
} from './worker-lodging.type';

const InitParams: GetWorkerLodgingListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const InitParamsProvider: GetProviderInfoListParams = {
	page: 1,
	pageSize: PageSize.S,
	type: Provider.FRANCHISE,
	name: '',
};

const InitApplicantListParams = {
	page: 1,
	pageSize: PageSize.S,
	programId: '' as string | number,
};

const initialState = {
	fetching: false,
	excelListFetching: false,
	workerLodgingList: [] as IWorkerLodgingListItemStore[],
	params: InitParams,
	paramsProvider: InitParamsProvider,
	total: 0,
	workerLodgingDetail: {} as IWorkerLodgingDetailStore,
	providerInfoList: [] as IProviderInfoItemStore[],
	providerInfoListTotal: 0,
	applicantList: [] as IWorkerLodgingApplicantDetailStore[],

	applicantListParams: InitApplicantListParams,
	applicantListTotal: 0,
	lodgingUploadFailedList: [] as TUploadExcelLodgingFailedItemStore[],
	uploadExcelResult: {
		successCount: 0,
		failCount: 0,
	},
	dataLodging: {} as TValueLodging,
};

const workerLodgingSlice = createSlice({
	initialState,
	name: 'workerLodging',
	reducers: {
		/**---- GET WORKER_LODGING LIST START ---- */
		getWorkerLodgingListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerLodgingListRequest>
		) => {
			state.fetching = true;
			state.params = action?.payload;
		},
		getWorkerLodgingListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerLodgingListSuccess>
		) => {
			const { workerLodgingList, total } = action.payload;
			state.fetching = false;
			state.workerLodgingList = workerLodgingList;
			state.total = total;
		},
		getWorkerLodgingListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerLodgingList = [];
		},

		getWorkerLodgingExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerLodgingExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerLodgingExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerLodgingExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER_LODGING LIST END ---- */

		/**---- GET WORKER_LODGING DETAIL START ---- */
		getWorkerLodgingDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerLodgingDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerLodgingDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerLodgingDetailSuccess>
		) => {
			const { workerLodgingDetail } = action.payload;
			state.fetching = false;
			state.workerLodgingDetail = workerLodgingDetail;
		},
		getWorkerLodgingDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER_LODGING DETAIL END ---- */

		/**---- UPDATE WORKER_LODGING DETAIL START ---- */
		updateWorkerLodgingDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<WorkerLodgingItem>>
		) => {
			const { data } = action.payload;
			const workerLodgingDetail = { ...state.workerLodgingDetail };
			state.workerLodgingDetail = formatWorkerLodgingDetail(
				mergeItemUpdated<IWorkerLodgingDetailStore, WorkerLodgingItem>(
					workerLodgingDetail,
					data
				)
			);
		},
		/**---- UPDATE WORKER_LODGING DETAIL END ---- */

		/**---- UPDATE WORKER APPLICANT START ---- */
		updateWorkerLodgingSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<WorkerLodgingListItem>>
		) => {
			const { data } = action.payload;
			const workerLodgingList = [...state.workerLodgingList];

			state.workerLodgingList = formatWorkerLodgingList(
				mergeListItemUpdated<
					IWorkerLodgingListItemStore,
					WorkerLodgingListItem
				>(workerLodgingList, data)
			);
		},
		/**---- UPDATE WORKER APPLICANT END ---- */

		/**---- GET RECOMMENDER APPLICANT START ---- */
		getWorkerLodgingApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerLodgingApplicantListRequest>
		) => {
			state.fetching = true;
			state.applicantListParams = {
				...action.payload,
				programId: action.payload.programId ?? '',
			};
		},
		getWorkerLodgingApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerLodgingApplicantListSuccess>
		) => {
			const { applicantList, applicantTotal } = action.payload;
			state.fetching = false;
			state.applicantList = applicantList;
			state.applicantListTotal = applicantTotal;
		},
		getWorkerLodgingApplicantListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET RECOMMENDER APPLICANT END ---- */

		/**---- UPDATE WORKER LODGING APPLICANT START ---- */
		updateWorkerLodgingApplicantSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IWorkerLodgingApplicantItem>>
		) => {
			const { data } = action.payload;
			const applicantList = [...state.applicantList];

			state.applicantList = formatWorkerLodgingApplicantList(
				mergeListItemUpdated<
					IWorkerLodgingApplicantDetailStore,
					IWorkerLodgingApplicantItem
				>(applicantList, data)
			);
		},
		/**---- UPDATE WORKER LODGING APPLICANT END ---- */

		/**---- DELETE WORKER_LODGING START ---- */
		deleteWorkerLodgingsRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerLodgingsRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerLodgingsSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerLodgingsFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_LODGING END ---- */

		/**---- GET PROVIDER INFO LIST START ---- */
		getProviderInfoListRequest: (
			state,
			action: PayloadAction<IPayloadGetProviderInfoListRequest>
		) => {
			state.fetching = true;
			state.paramsProvider = action?.payload.params;
		},
		getProviderInfoListSuccess: (
			state,
			action: PayloadAction<IPayloadGetProviderInfoListSuccess>
		) => {
			const { providerInfoList, total } = action.payload;

			state.fetching = false;
			state.providerInfoList = providerInfoList;
			state.providerInfoListTotal = total;
		},
		getProviderInfoListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET PROVIDER INFO LIST END ---- */

		/**---- CREATE LODGING START ---- */
		createLodgingRequest: (
			state,
			action: PayloadAction<IPayloadCreateLodgingRequest>
		) => {
			state.fetching = true;
		},
		createLodgingSuccess: (state) => {
			state.fetching = false;
		},
		createLodgingFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE LODGING END ---- */

		/**---- DELETE WORKER LODGING BY ID START ---- */
		deleteWorkerLodgingByIdRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerLodgingByIdRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerLodgingByIdSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerLodgingByIdFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER LODGING BY ID END ---- */

		/**---- GET APPLICANT LIST BY WORKER LODGING BY ID START ---- */
		getApplicantListByLodgingIdRequest: (
			state,
			action: PayloadAction<IPayloadGetApplicantListByLodgingIdRequest>
		) => {
			// const { params } = action.payload;

			state.fetching = true;
			state.applicantList = [];
			// state.applicantListParams = params;
		},
		getApplicantListByLodgingIdSuccess: (
			state,
			action: PayloadAction<IPayloadGetApplicantListByLodgingIdSuccess>
		) => {
			// const { data, total } = action.payload;
			// state.applicantList = data;
			// state.fetching = false;
			// state.applicantListTotal = total;
		},
		getApplicantListByLodgingIdFailure: (state) => {
			state.fetching = false;
			state.applicantListParams = InitApplicantListParams;
			state.applicantListTotal = 0;
		},
		/**---- GET APPLICANT LIST BY WORKER LODGING BY ID END ---- */

		/**---- CREATE LODGING APPLICANT START ---- */
		createLodgingApplicantRequest: (
			state,
			action: PayloadAction<IPayloadCreateLodgingApplicantRequest>
		) => {
			state.fetching = true;
		},
		createLodgingApplicantSuccess: (state) => {
			state.fetching = false;
		},
		createLodgingApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE LODGING APPLICANT END ---- */

		/**---- DELETE APPLICANT LIST BY LODGING ID START ---- */
		deleteApplicantListByLodgingIdRequest: (
			state,
			action: PayloadAction<IPayloadDeleteApplicantListByLodgingIdRequest>
		) => {
			state.fetching = true;
		},
		deleteApplicantListByLodgingIdSuccess: (state) => {
			state.fetching = false;
		},
		deleteApplicantListByLodgingIdFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE APPLICANT LIST BY LODGING ID END ---- */

		/**---- UPLOAD EXCEL WORKER_LODGING START ---- */
		uploadExcelLodgingRequest: (
			state,
			action: PayloadAction<TPayloadUploadExcelLodgingRequest>
		) => {
			state.fetching = true;
			state.lodgingUploadFailedList = [];
			state.uploadExcelResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		uploadExcelLodgingSuccess: (
			state,
			action: PayloadAction<TPayloadUploadExcelLodgingSuccess>
		) => {
			state.fetching = false;
			state.lodgingUploadFailedList = action.payload.failedList;
			state.uploadExcelResult = {
				successCount: action.payload.successCount || 0,
				failCount: action.payload.failCount || 0,
			};
		},
		uploadExcelLodgingFailure: (state) => {
			state.fetching = false;
			state.uploadExcelResult = {
				successCount: 0,
				failCount: 0,
			};
		},
		/**---- UPLOAD EXCEL WORKER_LODGING END ---- */

		/**---- UPDATE LODGING PROVIDER START ---- */
		updateLodgingProviderRequest: (
			state,
			action: PayloadAction<IPayloadUpdateLodgingProviderRequest>
		) => {
			state.fetching = true;
		},
		updateLodgingProviderSuccess: (state) => {
			state.fetching = false;
		},
		updateLodgingProviderFailure: (state) => {
			state.fetching = false;
		},
		/**---- UPDATE LODGING PROVIDER START ---- */
		saveDataLodging: (state, action: PayloadAction<TValueLodging>) => {
			state.dataLodging = action.payload;
		},
	},
});

export const WorkerLodgingSelectors = {
	selectFetching: (state: RootState) => state.workerLodging.fetching,
	selectTotal: (state: RootState) => state.workerLodging.total,
	selectWorkerLodgingList: (state: RootState) =>
		state.workerLodging.workerLodgingList,
	selectWorkerLodgingDetail: (state: RootState) =>
		state.workerLodging.workerLodgingDetail,
	selectParams: (state: RootState) => state.workerLodging.params,
	selectProviderInfoList: (state: RootState) =>
		state.workerLodging.providerInfoList,
	selectTotalProviderInfo: (state: RootState) =>
		state.workerLodging.providerInfoListTotal,
	selectParamsProvider: (state: RootState) =>
		state.workerLodging.paramsProvider,
	selectLodgingApplicantList: (state: RootState) =>
		state.workerLodging.applicantList,
	selectApplicantListParams: (state: RootState) =>
		state.workerLodging.applicantListParams,
	selectApplicantListTotal: (state: RootState) =>
		state.workerLodging.applicantListTotal,
	selectFailedList: (state: RootState) =>
		state.workerLodging.lodgingUploadFailedList,
	selectUploadExcelResult: (state: RootState) =>
		state.workerLodging.uploadExcelResult,
	selectDataLodging: (state: RootState) => state.workerLodging.dataLodging,
};

export const WorkerLodgingActions = workerLodgingSlice.actions;

export const WORKER_LODGING_PERSIST_KEY = 'workerLodgingPersist';
export const WorkerLodgingReducer = persistReducer(
	{
		key: WORKER_LODGING_PERSIST_KEY,
		storage: storage,
		whitelist: ['workerLodgingDetail', 'applicantList'],
	},
	workerLodgingSlice.reducer
);
